import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  Box,
} from '@mui/material';
import { identityApi } from '../api/identityApi'; // Import identityApi client
import { logError } from '../utils/logging'; // Import logError

function Login() {
  const navigate = useNavigate();

  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      const { email, password } = formData;
      const data = await identityApi.login(email, password);
      
      // Store the token and user email
      localStorage.setItem('token', data.access_token);
      localStorage.setItem('userEmail', email);

      // Dispatch auth change event to update navbar
      window.dispatchEvent(new Event('authChange'));

      // Redirect to dashboard
      navigate('/dashboard');
    } catch (err) {
      setError(err.message);
      logError(err, { context: 'Login handleSubmit' });
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        position: 'relative',
        background: 'linear-gradient(180deg, #0a0a0a 0%, #1a1a1a 50%, #0a0a0a 100%)',
      }}
    >
      {/* Background gradient overlay */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 50% 0%, rgba(25, 118, 210, 0.15) 0%, transparent 50%)',
          pointerEvents: 'none',
          zIndex: 0,
        }}
      />
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          width: '100vw',
          left: '50%',
          right: '50%',
          marginLeft: '-50vw',
          marginRight: '-50vw',
          py: 8,
          zIndex: 1,
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: '100%',
            px: { xs: 4, sm: 5, md: 6 },
            mx: 'auto',
            position: 'relative',
            zIndex: 1,
          }}
        >
          <Box
            sx={{
              maxWidth: '500px',
              mx: 'auto',
            }}
          >
            <Paper 
              elevation={0} 
              sx={{ 
                p: 4, 
                bgcolor: 'rgba(26, 26, 26, 0.8)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: 2,
              }}
            >
          <Typography 
            variant="h4" 
            component="h1" 
            gutterBottom 
            align="center"
            sx={{ 
              color: 'rgba(255, 255, 255, 0.95)',
              fontWeight: 600,
              mb: 3
            }}
          >
            Login
          </Typography>
          
          {error && (
            <Alert severity="error" sx={{ mb: 3 }} data-test-id="login-error">
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit} data-test-id="login-form">
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <TextField
                required
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                inputProps={{ 'data-test-id': 'login-email' }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: 'rgba(255, 255, 255, 0.05)',
                    '& fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.2)',
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.3)',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#1976d2',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(255, 255, 255, 0.7)',
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#1976d2',
                  },
                  '& .MuiOutlinedInput-input': {
                    color: 'rgba(255, 255, 255, 0.95)',
                  },
                }}
              />
              
              <TextField
                required
                fullWidth
                label="Password"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                inputProps={{ 'data-test-id': 'login-password' }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: 'rgba(255, 255, 255, 0.05)',
                    '& fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.2)',
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.3)',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#1976d2',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(255, 255, 255, 0.7)',
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#1976d2',
                  },
                  '& .MuiOutlinedInput-input': {
                    color: 'rgba(255, 255, 255, 0.95)',
                  },
                }}
              />

              <Button
                type="submit"
                variant="contained"
                color="primary"
                size="large"
                fullWidth
                data-test-id="login-submit"
                sx={{
                  mt: 1,
                  py: 1.5,
                  fontSize: '1rem',
                  fontWeight: 600,
                  textTransform: 'none',
                }}
              >
                Login
              </Button>

              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Link to="/forgot-password" style={{ textDecoration: 'none' }} data-test-id="login-forgot-password">
                  <Typography 
                    variant="body2"
                    sx={{ 
                      color: 'rgba(255, 255, 255, 0.7)',
                      cursor: 'pointer', 
                      '&:hover': { 
                        color: '#1976d2',
                        textDecoration: 'underline'
                      },
                      fontWeight: 500,
                      fontSize: '0.9rem'
                    }}
                  >
                    Forgot Password?
                  </Typography>
                </Link>
              </Box>
              
              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  Don't have an account?{' '}
                  <Link to="/register" style={{ textDecoration: 'none' }} data-test-id="login-register-link">
                    <Typography 
                      component="span" 
                      variant="body2" 
                      sx={{ 
                        color: '#1976d2',
                        fontWeight: 500,
                        '&:hover': {
                          textDecoration: 'underline'
                        }
                      }}
                    >
                      Register here
                    </Typography>
                  </Link>
                </Typography>
              </Box>
            </Box>
          </form>
        </Paper>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default Login;